<script setup lang="ts">
import type { LoginParam } from '@/types/user'
import type { FormInstance, FormRules } from 'element-plus'
import { useUserInfoStore } from '@/stores/modules/user'
import { register, sendCode, resetPassword } from '@/api/user'
import { onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import router from '@/router'
import { User, Lock, Message, Phone, Key, DataAnalysis } from '@element-plus/icons-vue'

const userInfo = useUserInfoStore()

// 当前Tab：login / register / forgot
const activeTab = ref('login')

// ======== 登录 ========
const loginFormRef = ref<FormInstance>()
const loginLoading = ref(false)
const loginData = reactive<LoginParam>({ username: '', password: '' })
const loginRules = reactive<FormRules>({
  username: [{ required: true, message: '请输入用户名', trigger: 'blur' }],
  password: [{ required: true, message: '请输入密码', trigger: 'blur' }]
})

const handleLogin = async () => {
  const valid = await loginFormRef.value?.validate()
  if (!valid) return
  loginLoading.value = true
  userInfo
    .login(loginData)
    .then(async () => {
      await userInfo.getUserInfo()
      const redirect = router.currentRoute.value.query.redirect as string
      router.push(redirect || '/admin/dashboard')
    })
    .catch(() => {})
    .finally(() => (loginLoading.value = false))
}

// ======== 注册 ========
const regFormRef = ref<FormInstance>()
const regLoading = ref(false)
const regData = reactive({
  nickname: '',
  username: '',
  password: '',
  confirmPassword: '',
  email: '',
  mobilephone: ''
})
const regRules = reactive<FormRules>({
  nickname: [{ required: true, message: '请输入昵称', trigger: 'blur' }],
  username: [
    { required: true, message: '请输入用户名', trigger: 'blur' },
    { min: 3, max: 20, message: '用户名长度3-20个字符', trigger: 'blur' }
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' },
    { min: 6, max: 20, message: '密码长度6-20个字符', trigger: 'blur' }
  ],
  confirmPassword: [
    { required: true, message: '请确认密码', trigger: 'blur' },
    {
      validator: (_rule: any, value: string, callback: any) => {
        if (value !== regData.password) callback(new Error('两次密码不一致'))
        else callback()
      },
      trigger: 'blur'
    }
  ],
  email: [
    { required: true, message: '请输入邮箱', trigger: 'blur' },
    { type: 'email', message: '邮箱格式不正确', trigger: 'blur' }
  ],
  mobilephone: [
    { required: true, message: '请输入手机号', trigger: 'blur' },
    { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', trigger: 'blur' }
  ]
})

const handleRegister = async () => {
  const valid = await regFormRef.value?.validate()
  if (!valid) return
  regLoading.value = true
  register({
    nickname: regData.nickname,
    username: regData.username,
    password: regData.password,
    email: regData.email,
    mobilephone: regData.mobilephone
  })
    .then(() => {
      ElMessage.success('注册成功，请登录')
      activeTab.value = 'login'
      loginData.username = regData.username
    })
    .catch(() => {})
    .finally(() => (regLoading.value = false))
}

// ======== 找回密码 ========
const forgotFormRef = ref<FormInstance>()
const forgotLoading = ref(false)
const codeSending = ref(false)
const codeCountdown = ref(0)
const forgotData = reactive({ email: '', code: '', newPassword: '' })
const forgotRules = reactive<FormRules>({
  email: [
    { required: true, message: '请输入邮箱', trigger: 'blur' },
    { type: 'email', message: '邮箱格式不正确', trigger: 'blur' }
  ],
  code: [{ required: true, message: '请输入验证码', trigger: 'blur' }],
  newPassword: [
    { required: true, message: '请输入新密码', trigger: 'blur' },
    { min: 6, max: 20, message: '密码长度6-20个字符', trigger: 'blur' }
  ]
})

const handleSendCode = async () => {
  if (!forgotData.email) {
    ElMessage.warning('请先输入邮箱')
    return
  }
  codeSending.value = true
  try {
    await sendCode(forgotData.email)
    ElMessage.success('验证码已发送')
    codeCountdown.value = 60
    const timer = setInterval(() => {
      codeCountdown.value--
      if (codeCountdown.value <= 0) clearInterval(timer)
    }, 1000)
  } catch {}
  codeSending.value = false
}

const handleResetPassword = async () => {
  const valid = await forgotFormRef.value?.validate()
  if (!valid) return
  forgotLoading.value = true
  resetPassword({
    email: forgotData.email,
    code: forgotData.code,
    newPassword: forgotData.newPassword
  })
    .then(() => {
      ElMessage.success('密码重置成功，请登录')
      activeTab.value = 'login'
    })
    .catch(() => {})
    .finally(() => (forgotLoading.value = false))
}

// 回车登录
const onKeyUp = (e: KeyboardEvent) => {
  if (e.key === 'Enter' && activeTab.value === 'login') handleLogin()
}
onMounted(() => window.addEventListener('keyup', onKeyUp))
onBeforeUnmount(() => window.removeEventListener('keyup', onKeyUp))
</script>

<template>
  <div class="login-page">
    <!-- 背景装饰 -->
    <div class="bg-decor">
      <div class="circle c1"></div>
      <div class="circle c2"></div>
      <div class="circle c3"></div>
    </div>

    <div class="login-card">
      <!-- 左侧品牌区 -->
      <div class="brand-side">
        <div class="brand-content">
          <div class="brand-icon">
            <el-icon :size="56"><DataAnalysis /></el-icon>
          </div>
          <h1 class="brand-title">甲醛测试仪管理后台</h1>
          <p class="brand-sub">大华测控智能监测平台</p>
          <div class="brand-features">
            <div class="feature-item">
              <span class="dot"></span>
              <span>实时甲醛浓度检测</span>
            </div>
            <div class="feature-item">
              <span class="dot"></span>
              <span>多设备统一管理</span>
            </div>
            <div class="feature-item">
              <span class="dot"></span>
              <span>检测数据智能分析</span>
            </div>
            <div class="feature-item">
              <span class="dot"></span>
              <span>超标报警实时推送</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧表单区 -->
      <div class="form-side">
        <el-tabs v-model="activeTab" class="login-tabs">
          <!-- 登录 -->
          <el-tab-pane label="登录" name="login">
            <h2 class="form-title">欢迎回来</h2>
            <p class="form-desc">请输入您的账号信息登录系统</p>
            <el-form ref="loginFormRef" :rules="loginRules" :model="loginData" size="large">
              <el-form-item prop="username">
                <el-input v-model="loginData.username" placeholder="请输入用户名" :prefix-icon="User" />
              </el-form-item>
              <el-form-item prop="password">
                <el-input
                  type="password"
                  v-model="loginData.password"
                  placeholder="请输入密码"
                  show-password
                  :prefix-icon="Lock"
                />
              </el-form-item>
              <el-form-item>
                <el-button class="submit-btn" type="primary" @click="handleLogin" :loading="loginLoading">
                  登 录
                </el-button>
              </el-form-item>
            </el-form>
            <div class="form-footer">
              <span>还没有账号？</span>
              <el-link type="primary" @click="activeTab = 'register'">立即注册</el-link>
              <el-divider direction="vertical" />
              <el-link type="info" @click="activeTab = 'forgot'">忘记密码</el-link>
            </div>
          </el-tab-pane>

          <!-- 注册 -->
          <el-tab-pane label="注册" name="register">
            <h2 class="form-title">创建账号</h2>
            <p class="form-desc">填写以下信息注册新账号</p>
            <el-form ref="regFormRef" :rules="regRules" :model="regData" size="large">
              <el-form-item prop="nickname">
                <el-input v-model="regData.nickname" placeholder="昵称" :prefix-icon="DataAnalysis" />
              </el-form-item>
              <el-form-item prop="username">
                <el-input v-model="regData.username" placeholder="用户名（3-20个字符）" :prefix-icon="User" />
              </el-form-item>
              <el-form-item prop="password">
                <el-input
                  type="password"
                  v-model="regData.password"
                  placeholder="密码（6-20个字符）"
                  show-password
                  :prefix-icon="Lock"
                />
              </el-form-item>
              <el-form-item prop="confirmPassword">
                <el-input
                  type="password"
                  v-model="regData.confirmPassword"
                  placeholder="确认密码"
                  show-password
                  :prefix-icon="Lock"
                />
              </el-form-item>
              <el-form-item prop="email">
                <el-input v-model="regData.email" placeholder="邮箱" :prefix-icon="Message" />
              </el-form-item>
              <el-form-item prop="mobilephone">
                <el-input v-model="regData.mobilephone" placeholder="手机号" :prefix-icon="Phone" />
              </el-form-item>
              <el-form-item>
                <el-button class="submit-btn" type="primary" @click="handleRegister" :loading="regLoading">
                  注 册
                </el-button>
              </el-form-item>
            </el-form>
            <div class="form-footer">
              <span>已有账号？</span>
              <el-link type="primary" @click="activeTab = 'login'">返回登录</el-link>
            </div>
          </el-tab-pane>

          <!-- 找回密码 -->
          <el-tab-pane label="找回密码" name="forgot">
            <h2 class="form-title">重置密码</h2>
            <p class="form-desc">通过邮箱验证码重置您的密码</p>
            <el-form ref="forgotFormRef" :rules="forgotRules" :model="forgotData" size="large">
              <el-form-item prop="email">
                <el-input v-model="forgotData.email" placeholder="注册时使用的邮箱" :prefix-icon="Message" />
              </el-form-item>
              <el-form-item prop="code">
                <div class="code-row">
                  <el-input v-model="forgotData.code" placeholder="验证码" :prefix-icon="Key" />
                  <el-button
                    type="primary"
                    plain
                    :disabled="codeCountdown > 0"
                    :loading="codeSending"
                    @click="handleSendCode"
                    class="code-btn"
                  >
                    {{ codeCountdown > 0 ? `${codeCountdown}s` : '发送验证码' }}
                  </el-button>
                </div>
              </el-form-item>
              <el-form-item prop="newPassword">
                <el-input
                  type="password"
                  v-model="forgotData.newPassword"
                  placeholder="新密码（6-20个字符）"
                  show-password
                  :prefix-icon="Lock"
                />
              </el-form-item>
              <el-form-item>
                <el-button class="submit-btn" type="primary" @click="handleResetPassword" :loading="forgotLoading">
                  重置密码
                </el-button>
              </el-form-item>
            </el-form>
            <div class="form-footer">
              <el-link type="primary" @click="activeTab = 'login'">返回登录</el-link>
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>
    </div>

    <!-- 底部版权 -->
    <div class="login-footer">大华测控甲醛测试仪管理后台 v1.0</div>
  </div>
</template>

<style scoped lang="scss">
.login-page {
  @apply min-h-screen w-full flex items-center justify-center relative overflow-hidden;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
}

/* 背景装饰圆 */
.bg-decor {
  @apply absolute inset-0 pointer-events-none;
  .circle {
    @apply absolute rounded-full opacity-10;
  }
  .c1 {
    @apply w-[600px] h-[600px] -top-40 -left-40;
    background: radial-gradient(circle, #3b82f6, transparent);
  }
  .c2 {
    @apply w-[400px] h-[400px] -bottom-20 -right-20;
    background: radial-gradient(circle, #ef4444, transparent);
  }
  .c3 {
    @apply w-[300px] h-[300px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2;
    background: radial-gradient(circle, #f59e0b, transparent);
  }
}

/* 主卡片 */
.login-card {
  @apply relative z-10 flex rounded-2xl overflow-hidden shadow-2xl;
  width: 900px;
  max-width: 95vw;
  min-height: 560px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* 左侧品牌区 */
.brand-side {
  @apply flex items-center justify-center;
  width: 380px;
  background: linear-gradient(180deg, #1e3a5f 0%, #0f2744 100%);
  flex-shrink: 0;

  @media (max-width: 768px) {
    @apply hidden;
  }
}

.brand-content {
  @apply text-center px-10;
}

.brand-icon {
  @apply w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center;
  background: linear-gradient(135deg, #ef4444, #f97316);
  box-shadow: 0 8px 32px rgba(239, 68, 68, 0.4);
  color: white;
}

.brand-title {
  @apply text-3xl font-bold text-white mb-2;
}

.brand-sub {
  @apply text-gray-300 mb-8;
  /* 10个汉字宽度对齐上面9个汉字 text-3xl(30px)：10×27 ≈ 9×30 */
  font-size: 27px;
  letter-spacing: 1px;
}

.brand-features {
  @apply text-left space-y-3;
  .feature-item {
    @apply flex items-center text-gray-300 text-sm;
    .dot {
      @apply w-2 h-2 rounded-full mr-3 flex-shrink-0;
      background: linear-gradient(135deg, #ef4444, #f97316);
    }
  }
}

/* 右侧表单区 */
.form-side {
  @apply flex-1 bg-white p-10 overflow-y-auto;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 768px) {
    @apply p-6;
  }
}

.form-title {
  @apply text-2xl font-bold text-gray-800 mb-1;
}

.form-desc {
  @apply text-gray-400 text-sm mb-6;
}

/* Tabs 样式 */
:deep(.login-tabs) {
  .el-tabs__header {
    margin-bottom: 0;
  }
  .el-tabs__nav-wrap::after {
    display: none;
  }
  .el-tabs__item {
    @apply text-gray-400;
    font-size: 15px;
    &.is-active {
      @apply text-blue-600 font-semibold;
    }
  }
  .el-tabs__active-bar {
    background: linear-gradient(90deg, #3b82f6, #60a5fa);
  }
  .el-tabs__content {
    /* 固定内容区高度，避免切换Tab时高度跳动 */
    height: 520px;
    overflow: hidden;
  }
  .el-tab-pane {
    height: 100%;
  }
}

/* 提交按钮 */
.submit-btn {
  @apply w-full h-11 rounded-lg text-base font-medium;
  background: linear-gradient(135deg, #1e3a5f, #3b82f6);
  border: none;
  &:hover {
    background: linear-gradient(135deg, #2a4a70, #4b92f7);
  }
}

/* 验证码行 */
.code-row {
  @apply flex w-full gap-3;
  .el-input {
    flex: 1;
  }
  .code-btn {
    flex-shrink: 0;
    width: 130px;
  }
}

/* 表单底部链接 */
.form-footer {
  @apply text-center text-sm text-gray-400 mt-4;
  .el-link {
    font-size: 13px;
  }
}

/* 底部版权 */
.login-footer {
  @apply absolute bottom-4 text-gray-500 text-xs z-10;
}
</style>
